import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import I18n from 'i18n-js';
import { Drawer, Input } from 'grab-ui';
import PropTypes from 'prop-types';
import { CityPropType } from 'common/prop-types';
import _get from 'lodash/get';
import cx from 'classnames';
import closeIcon from 'public/icons/close.svg';
import { makeSearchAddressFunc } from 'helper/address-search';
import AddressField from 'components/v2/address-field/new';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import MapWithDragSearch from 'components/v2/map-with-drag-search';
import OverlayBody from 'components/v2/anytime-booking/overlay/overlay-body';
import {
  isPaxSelector,
  selectedCitySelector,
  userCompanySelector,
} from 'selectors/app-selector';
import {
  COLUMN_SENDER_ADDRESS,
  COLUMN_RECEIVER_ADDRESS,
  COLUMN_SENDER_ADDRESS_DETAIL,
  COLUMN_RECEIVER_ADDRESS_DETAIL,
  SENDER_POI,
  DROP_OFF_POI,
} from 'constants/new-bulk-upload-const';
import { getExcelReduxName } from 'components/v2/anytime-booking/common/new-bulk-upload-common';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';

import styles from './edit-address-drawer.less';

const EditAddressDrawer = ({
  form,
  dispatch,
  editingAddress,
  rawData,
  city,
  isPax,
  company,
  setEditingAddress,
}) => {
  const { rowID, isPickUp } = editingAddress || {};
  const row = rawData.find(row => row.id === rowID);
  const addressLocationFieldName = isPickUp
    ? getExcelReduxName(COLUMN_SENDER_ADDRESS)
    : getExcelReduxName(COLUMN_RECEIVER_ADDRESS);
  const addressLocationNoteFieldName = isPickUp
    ? getExcelReduxName(COLUMN_SENDER_ADDRESS_DETAIL)
    : getExcelReduxName(COLUMN_RECEIVER_ADDRESS_DETAIL);
  const poiFieldName = isPickUp ? SENDER_POI : DROP_OFF_POI;
  const poi = _get(row, poiFieldName);
  const displayMapLatLng = _get(poi, 'latlng');
  const noteFieldID = `${rowID}/${addressLocationNoteFieldName}`;
  const noteFieldError = form.getFieldError(noteFieldID);

  const fetchPickUpAddressFunc = useMemo(
    () =>
      makeSearchAddressFunc({
        isPax,
        city,
        company,
        isPickUp: true,
      }),
    [isPax, city, company],
  );
  const fetchDropOffAddressFunc = useMemo(
    () =>
      makeSearchAddressFunc({
        isPax,
        city,
        company,
        isPickUp: false,
      }),
    [isPax, city, company],
  );
  const fetchAddressFunc = isPickUp
    ? fetchPickUpAddressFunc
    : fetchDropOffAddressFunc;

  const onSelectPOI = useCallback(
    poi => {
      if (rowID) {
        dispatch(
          NewBulkUploadDuck.creators.editRawData(rowID, poiFieldName, poi),
        );
        dispatch(
          scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2EditAddressDrawerAddress(
            _get(poi, ['address', 'combined_address']),
          ),
        );
      }
    },
    [rowID, poiFieldName],
  );

  const onAddressNoteChange = useCallback(
    e => {
      if (e && e.target) {
        form.setFieldsValue({
          [noteFieldID]: e.target.value,
        });
        form.validateFields();
        dispatch(
          scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2EditAddressDrawerLocationNotes(),
        );
      }
    },
    [form, noteFieldID],
  );

  const onClickConfirm = useCallback(
    () => {
      setEditingAddress(undefined);
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2EditAddressDrawerConfirm(
          _get(row, addressLocationFieldName),
          _get(poi, ['address', 'combined_address']),
        ),
      );
    },
    [row, poi, setEditingAddress],
  );

  const onClickClose = useCallback(
    () => {
      setEditingAddress(undefined);
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2EditAddressDrawerCancel(
          _get(row, addressLocationFieldName),
        ),
      );
    },
    [row, poi, setEditingAddress],
  );

  return (
    <Drawer
      key={'editAddressDrawer'}
      width={495}
      visible={!!editingAddress}
      placement={'right'}
      className={styles.overlayDrawer}
      height="100%"
      onClose={onClickClose}
      destroyOnClose
    >
      <OverlayBody showConfirm onConfirmClick={onClickConfirm}>
        <a className={styles.closeButton} onClick={onClickClose}>
          <img src={closeIcon} alt="close" />
        </a>
        <MapWithDragSearch
          key={'drag-map'}
          city={city}
          latlng={displayMapLatLng}
          showPinIndex={false}
          isSender={isPickUp}
          onMapSearchResult={onSelectPOI}
        />
        <div className={styles.addressFieldWithNote}>
          <div className={styles.addressField}>
            <div className={styles.addressFieldContainer}>
              <AddressField
                city={city}
                onChange={onSelectPOI}
                poi={poi}
                isPickUp={isPickUp}
                showNumbers={false}
                presetKeyword={_get(row, addressLocationFieldName)}
                fetchAddressFunc={fetchAddressFunc}
                allowDelete={false}
                placeholder={
                  isPickUp
                    ? I18n.t('v2.locationDrawer.placeholder.addPickupLocation')
                    : I18n.t('v2.locationDrawer.placeholder.addDropOffLocation')
                }
                autoFocus
              />
            </div>
          </div>
          <Input
            className={cx(styles.addressNoteField, {
              [styles.error]: !!noteFieldError,
            })}
            placeholder={I18n.t(
              'v2.homepage.manualOrder.placeholder.locationNote',
            )}
            value={_get(row, addressLocationNoteFieldName)}
            onChange={onAddressNoteChange}
          />
          <div className={styles.addressNoteFieldError}> {noteFieldError} </div>
        </div>
      </OverlayBody>
    </Drawer>
  );
};

EditAddressDrawer.defaultProps = {
  company: undefined,
  editingAddress: undefined,
  city: undefined,
};

EditAddressDrawer.propTypes = {
  form: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  editingAddress: PropTypes.shape({}),
  rawData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  city: CityPropType,
  isPax: PropTypes.bool.isRequired,
  company: PropTypes.shape({}),
  setEditingAddress: PropTypes.func.isRequired,
};

export default connect(state => ({
  isPax: isPaxSelector(state),
  company: userCompanySelector(state),
  city: selectedCitySelector(state),
}))(EditAddressDrawer);
